import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Tierliste } from '../../gruppe-tier/classes/tierliste';
import { Haendler } from '../../stammdaten/haendler/haendler';
import { Betriebskontakt } from '../../stammdaten/betriebskontakte/betriebskontakt';
import { HaendlerDetailsDialogComponent } from '../../stammdaten/haendler/haendler-details-dialog/haendler-details-dialog.component';
import { HaendlerSelectorComponent } from '../../stammdaten/haendler/haendler-selector/haendler-selector.component';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { HttpParams } from '@angular/common/http';
import { MessageService } from '../../../services/message-service/message.service';
import { BetriebskontakteService } from '../../stammdaten/betriebskontakte/betriebskontakte.service';
import { KieneUtils } from '../../../utils/KieneUtils';

const HIT_MELDEN_SPEICHER = 'hit_melden';
const REKLAMATIONEN_UEBERNEHMEN_SPEICHER = 'reklamationen_uebernehmen';

@Component({
    selector: 'kiene-import-optionen-dialog',
    templateUrl: './import-optionen-dialog.component.html',
    styleUrls: ['./import-optionen-dialog.component.scss'],
})
export class ImportOptionenDialogComponent implements OnInit, OnDestroy {
    einstalldatumCtrl = new UntypedFormControl(undefined, [Validators.required]);
    hitMeldenCtrl = new UntypedFormControl(1);
    reklamationenUebernehmenCtrl = new UntypedFormControl(0);
    kfzCtrl = new FormControl();
    haendlerCtrl = new UntypedFormControl();
    gewichtModusCtrl = new FormControl<'all' | 'single' | 'total' | null>('total');
    gewichtAlleSelektiertCtrl = new FormControl();
    gewichtGesamtCtrl = new FormControl();
    selectedTiere: {
        gt_id: number;
        tier_id?: number;
        ohrmarke_notnull: string;
        gewichtCtrl: FormControl;
        currentGsId?: number;
    }[] = [];
    canLoadTransferWeight = false;
    canLoadReklamationen = false;
    private senderReklamationenErlaubt = false;
    private reklamationenSpeicherSub?: Subscription;
    private reklamationenCtrlSub?: Subscription;
    private hitMeldenSpeicherSub?: Subscription;

    tierliste: Tierliste;
    selectedBetriebskontakt: Betriebskontakt | null = null;

    @ViewChild('haendlerSelector') haendlerSelector: HaendlerSelectorComponent;

    constructor(
        public dialogRef: MatDialogRef<ImportOptionenDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data,
        private matDialog: MatDialog,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneBackendApiService,
        private messageService: MessageService,
        private betriebskontakteService: BetriebskontakteService,
    ) {
        this.tierliste = this.data.tierliste;
    }

    ngOnInit(): void {
        this.einstalldatumCtrl.setValue(this.tierliste.datum_abgang);
        this.kfzCtrl.setValue(this.tierliste.kfz);
        this.initSelectedTiereGewichte();
        this.ladeTiereAusAktuellerTierliste();
        this.loadGewichtTransferEligibility();
        this.initReklamationenUebernahme();
        this.loadHitMeldenPreference();

        // Legacy Händler-ValueChanges Subscription
        this.haendlerCtrl.valueChanges.subscribe((haendler: Haendler) => {
            this.setHaendler(haendler);
            // Wenn ein Händler ausgewählt wird, Betriebskontakt zurücksetzen
            if (haendler && haendler.viehhaendler_id) {
                this.selectedBetriebskontakt = null;
                this.tierliste.betriebskontakt_haendler_id = undefined;
            }
        });

        this.hydrateHaendlerFromTierliste();
    }

    ngOnDestroy(): void {
        this.reklamationenSpeicherSub?.unsubscribe();
        this.reklamationenCtrlSub?.unsubscribe();
        this.hitMeldenSpeicherSub?.unsubscribe();
    }

    private hydrateHaendlerFromTierliste(): void {
        const viehhaendlerId = Number(this.tierliste?.viehhaendler_id || 0);
        const kontaktId = Number(this.tierliste?.betriebskontakt_haendler_id || 0);
        if (viehhaendlerId > 0) {
            const haendler = new Haendler();
            haendler.viehhaendler_id = viehhaendlerId;
            haendler.name = this.tierliste.viehhaendler_name || '';
            this.haendlerCtrl.setValue(haendler, { emitEvent: false });
            this.setHaendler(haendler);
            return;
        }
        if (kontaktId > 0) {
            this.betriebskontakteService.getBetriebskontaktById(kontaktId).subscribe({
                next: kontakt => {
                    if (kontakt) {
                        this.onBetriebskontaktSelected(kontakt);
                    }
                },
            });
        }
    }

    close() {
        this.dialogRef.close(this.tierliste);
    }

    istDerselbeKunde() {
        return KieneUtils.isApiZero(this.tierliste?.tierliste_extern);
    }

    save() {
        const records = this.buildMostActualAbgangsgewichteRecords();
        const einstallRecords = this.buildEinstallgewichteRecords();
        const continueSave = () => {
            const gewichtModus = this.gewichtModusCtrl.value;
            this.tierliste.datum_einstallen = this.einstalldatumCtrl.value;
            const fallbackGewichtJeTier = this.toValidGewicht(this.gewichtAlleSelektiertCtrl.value);
            const totalOnlyGewicht = this.toValidGewicht(this.gewichtGesamtCtrl.value);
            const fallbackGesamtgewicht =
                !this.selectedTiere?.length &&
                fallbackGewichtJeTier !== null &&
                Number(this.tierliste?.anzahl_tiere || 0) > 0
                    ? Math.round(fallbackGewichtJeTier * Number(this.tierliste.anzahl_tiere) * 100) / 100
                    : null;
            this.tierliste.gewicht_gesamt_einstallen =
                gewichtModus === 'total'
                    ? totalOnlyGewicht ?? undefined
                    : gewichtModus
                      ? this.selectedTiereTotalGewicht ?? fallbackGesamtgewicht ?? undefined
                      : undefined;
            this.tierliste.kfz = this.kfzCtrl.value;
            this.tierliste.import_einstallgewichte_records = einstallRecords;
            this.tierliste.reklamationen_weitergeben =
                this.canLoadReklamationen && this.isSpeicherOne(this.reklamationenUebernehmenCtrl.value) ? 1 : 0;
            this.saveReklamationenUebernehmenPreference();
            if (this.istDerselbeKunde()) {
                this.tierliste.einstallen_melden = 0;
            } else {
                this.tierliste.einstallen_melden = this.hitMeldenCtrl.value;
                this.saveHitMeldenPreference();
            }

            // Stelle sicher, dass die Händler-Daten korrekt gesetzt sind
            this.setHaendler(this.haendlerCtrl.value);

            this.dialogRef.close(this.tierliste);
        };

        if (records.length > 0 && Number(this.tierliste?.gruppe_id_abgang) > 0) {
            this.api
                .post(this.apiBaseUrl + 'gruppe_standort/save_most_actual_abgangsgewichte.php', {
                    gruppe_id: Number(this.tierliste?.gruppe_id_abgang),
                    records: records,
                })
                .subscribe({
                    next: () => continueSave(),
                    error: err => {
                        this.messageService.errorMessage('Gewichte konnten nicht gespeichert werden!', err);
                    },
                });
            return;
        }
        continueSave();
    }

    setHaendler(haendler: Haendler) {
        console.log('DEBUG: setHaendler aufgerufen mit:', haendler);
        if (haendler) {
            this.tierliste.viehhaendler_id = haendler.viehhaendler_id;
            console.log('DEBUG: viehhaendler_id gesetzt auf:', haendler.viehhaendler_id);
        } else {
            this.tierliste.viehhaendler_id = undefined;
            console.log('DEBUG: viehhaendler_id auf undefined gesetzt');
        }
        console.log('DEBUG: tierliste.viehhaendler_id ist jetzt:', this.tierliste.viehhaendler_id);
    }

    onBetriebskontaktSelected(kontakt: Betriebskontakt) {
        this.selectedBetriebskontakt = kontakt;
        // Setze die betriebskontakt_haendler_id in der Tierliste
        if (kontakt) {
            this.tierliste.betriebskontakt_haendler_id = kontakt.kunde_id;
            // Wenn ein Betriebskontakt ausgewählt wird, Händler zurücksetzen
            this.haendlerCtrl.setValue(null);
            this.tierliste.viehhaendler_id = undefined;
            console.log('DEBUG: Betriebskontakt ausgewählt:', kontakt);
            console.log('DEBUG: kunde_id gesetzt:', kontakt.kunde_id);
        } else {
            this.tierliste.betriebskontakt_haendler_id = undefined;
        }
    }

    onBetriebskontaktEntfernen() {
        // Setze die Auswahl zurück
        this.selectedBetriebskontakt = null;
        this.tierliste.betriebskontakt_haendler_id = undefined;
        console.log('DEBUG: Betriebskontakt entfernt');
    }

    openHaendlerVerwaltung() {
        const dialogRef = this.matDialog.open(HaendlerDetailsDialogComponent, {
            width: '90vw',
            minWidth: '90vw',
            disableClose: false,
            autoFocus: false,
        });

        dialogRef.afterClosed().subscribe((result: Haendler) => {
            if (result) {
                // Neuer Händler wurde erstellt, aktualisiere die Händler-Liste
                this.haendlerSelector?.refreshHaendler();

                // Setze den neuen Händler als ausgewählt
                this.haendlerCtrl.setValue(result);
                console.log('Neuer Händler erstellt und ausgewählt:', result);
            }
        });
    }

    private initReklamationenUebernahme(): void {
        this.senderReklamationenErlaubt = KieneUtils.isApiOne(this.tierliste?.reklamationen_weitergeben);
        this.canLoadReklamationen =
            this.istDerselbeKunde() ||
            this.senderReklamationenErlaubt ||
            KieneUtils.isApiOne(this.tierliste?.transfer_innerhalb_betrieb);
        this.reklamationenUebernehmenCtrl.setValue(0, { emitEvent: false });
        this.reklamationenSpeicherSub?.unsubscribe();
        this.reklamationenSpeicherSub = this.api.getSpeicher(REKLAMATIONEN_UEBERNEHMEN_SPEICHER).subscribe({
            next: response => {
                if (!this.canLoadReklamationen) {
                    this.reklamationenUebernehmenCtrl.setValue(0, { emitEvent: false });
                    return;
                }
                if (response != null && response !== undefined) {
                    this.reklamationenUebernehmenCtrl.setValue(this.isSpeicherOne(response) ? 1 : 0, {
                        emitEvent: false,
                    });
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden der Reklamations-Übernahme!', err);
            },
        });
        this.reklamationenCtrlSub?.unsubscribe();
        this.reklamationenCtrlSub = this.reklamationenUebernehmenCtrl.valueChanges.subscribe(() => {
            this.saveReklamationenUebernehmenPreference();
        });
    }

    private loadHitMeldenPreference(): void {
        this.hitMeldenSpeicherSub?.unsubscribe();
        this.hitMeldenSpeicherSub = this.api.getSpeicher(HIT_MELDEN_SPEICHER).subscribe({
            next: response => {
                if (response != null && response !== undefined) {
                    this.hitMeldenCtrl.setValue(response);
                }
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Laden der HIT-Melden-Einstellung!', err);
            },
        });
    }

    private saveHitMeldenPreference(): void {
        this.api.setSpeicher(HIT_MELDEN_SPEICHER, this.hitMeldenCtrl.value).subscribe({
            error: err => {
                this.messageService.errorMessage('Fehler beim Speichern der HIT-Melden-Einstellung!', err);
            },
        });
    }

    private saveReklamationenUebernehmenPreference(): void {
        this.api
            .setSpeicher(REKLAMATIONEN_UEBERNEHMEN_SPEICHER, this.isSpeicherOne(this.reklamationenUebernehmenCtrl.value) ? 1 : 0)
            .subscribe({
                error: err => {
                    this.messageService.errorMessage('Fehler beim Speichern der Reklamations-Übernahme!', err);
                },
            });
    }

    private isSpeicherOne(value: unknown): boolean {
        return value === 1 || value === '1' || value === true;
    }

    alteGewichteUebernehmen(): void {
        if (!this.canLoadTransferWeight) {
            this.messageService.infoMessage(
                'Letztes Gewicht ist nur bei internen Übernahmen oder mit Erlaubnis des Absenders verfügbar.',
            );
            return;
        }
        const gtIds = this.selectedTiere.map(t => Number(t.gt_id)).filter(id => id > 0);
        if (!gtIds.length) {
            return;
        }
        let params = new HttpParams();
        if (Number(this.tierliste?.gruppe_id_abgang) > 0) {
            params = params.set('gruppe_id', String(this.tierliste.gruppe_id_abgang));
        }
        params = params.set('gt_ids', gtIds.join(','));
        params = params.set('tierliste_id', String(this.tierliste?.tierliste_id || 0));
        params = params.set('gruppe_id_einstallen', String(this.tierliste?.gruppe_id_einstallen || 0));
        this.api.getAll(this.apiBaseUrl + 'gruppe/read_gt_alte_gewichte.php', params).subscribe({
            next: response => {
                const records = response?.records || [];
                const weightByGtId = new Map<number, number>();
                for (const rec of records) {
                    const gtId = Number(rec?.gt_id);
                    const w = this.toValidGewicht(rec?.gewicht_alt);
                    if (gtId > 0 && w !== null) {
                        weightByGtId.set(gtId, w);
                    }
                }
                const gesetzt = this.applyGeladeneGewichte(weightByGtId);
                if (gesetzt > 0) {
                    this.messageService.infoMessage(`Letzte Gewichte fuer ${gesetzt} Tier(e) geladen.`);
                } else {
                    this.messageService.infoMessage('Keine letzten Gewichte gefunden.');
                }
            },
            error: err => {
                this.messageService.errorMessage('Letzte Gewichte konnten nicht geladen werden!', err);
            },
        });
    }

    private applyGeladeneGewichte(weightByGtId: Map<number, number>): number {
        const values = [...weightByGtId.values()].filter(w => w > 0);
        if (!values.length) {
            return 0;
        }
        const modus = this.gewichtModusCtrl.value;
        if (modus === 'total') {
            const sum = Math.round(values.reduce((a, b) => a + b, 0) * 100) / 100;
            this.gewichtGesamtCtrl.setValue(sum);
            return values.length;
        }
        if (modus === 'all') {
            const avg = Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100;
            this.gewichtAlleSelektiertCtrl.setValue(avg);
            for (const tier of this.selectedTiere) {
                const w = weightByGtId.get(tier.gt_id);
                if (w !== undefined) {
                    tier.gewichtCtrl.setValue(w, { emitEvent: false });
                } else {
                    tier.gewichtCtrl.setValue(avg, { emitEvent: false });
                }
            }
            return values.length;
        }
        let gesetzt = 0;
        for (const tier of this.selectedTiere) {
            const w = weightByGtId.get(tier.gt_id);
            if (w !== undefined) {
                tier.gewichtCtrl.setValue(w);
                gesetzt++;
            }
        }
        return gesetzt;
    }

    toggleGewichtModus(modus: 'all' | 'single' | 'total'): void {
        if (this.gewichtModusCtrl.value !== modus) {
            return;
        }
        setTimeout(() => {
            if (this.gewichtModusCtrl.value === modus) {
                this.gewichtModusCtrl.setValue(null);
            }
        });
    }

    private loadGewichtTransferEligibility(): void {
        const tierlisteId = Number(this.tierliste?.tierliste_id || 0);
        if (tierlisteId <= 0) {
            this.canLoadTransferWeight = this.istDerselbeKunde();
            return;
        }
        let params = new HttpParams();
        if (Number(this.tierliste?.gruppe_id_abgang) > 0) {
            params = params.set('gruppe_id', String(this.tierliste.gruppe_id_abgang));
        }
        params = params.set('tierliste_id', String(tierlisteId));
        params = params.set('gruppe_id_einstallen', String(this.tierliste?.gruppe_id_einstallen || 0));
        this.api.getAll(this.apiBaseUrl + 'gruppe/read_gt_alte_gewichte.php', params).subscribe({
            next: (response: any) => {
                this.canLoadTransferWeight = Number(response?.can_load_transfer_weight || 0) > 0;
            },
            error: () => {
                this.canLoadTransferWeight = this.istDerselbeKunde();
            },
        });
    }

    get selectedTiereTotalGewicht(): number | null {
        if (!this.gewichtModusCtrl.value) {
            return null;
        }
        if (this.gewichtModusCtrl.value === 'total') {
            return this.toValidGewicht(this.gewichtGesamtCtrl.value);
        }
        if (!this.selectedTiere?.length) {
            return null;
        }
        const werte = this.selectedTiere
            .map(t => Number(t.gewichtCtrl.value))
            .filter(v => !isNaN(v) && v > 0);
        if (!werte.length) {
            return null;
        }
        return Math.round(werte.reduce((sum, val) => sum + val, 0) * 100) / 100;
    }

    get selectedTiereCount(): number {
        return this.selectedTiere?.length || 0;
    }

    private initSelectedTiereGewichte(): void {
        this.gewichtAlleSelektiertCtrl.valueChanges.subscribe({
            next: value => {
                if (this.gewichtModusCtrl.value !== 'all') {
                    return;
                }
                for (const tier of this.selectedTiere) {
                    tier.gewichtCtrl.setValue(value, { emitEvent: false });
                }
            },
        });
    }

    private ladeTiereAusAktuellerTierliste(): void {
        const tierlisteId = Number(this.tierliste?.tierliste_id);
        if (tierlisteId <= 0) {
            return;
        }
        // Cross-farm imports: animals stay in the sender's group; use the dedicated import endpoint.
        if (!this.istDerselbeKunde()) {
            this.ladeTiereFuerImportFallback(tierlisteId);
            return;
        }
        const params = new HttpParams().set('tierliste_id', tierlisteId.toString());
        this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_kundenportal.php', params).subscribe({
            next: response => {
                const records = response?.records || [];
                this.selectedTiere = records
                    .filter(t => Number(t?.gt_id) > 0)
                    .map(t => ({
                        gt_id: Number(t.gt_id),
                        tier_id: Number((t as any)?.tier_id) > 0 ? Number((t as any).tier_id) : undefined,
                        ohrmarke_notnull: t.ohrmarke_notnull || `Tier ${t.gt_id}`,
                        gewichtCtrl: new FormControl(this.toValidGewicht((t as any)?.abgangsgewicht)),
                        currentGsId: Number((t as any)?.gs_id) > 0 ? Number((t as any).gs_id) : undefined,
                    }));
                if (this.selectedTiere.length === 0) {
                    this.ladeTiereFuerImportFallback(tierlisteId);
                }
            },
            error: () => {
                this.ladeTiereFuerImportFallback(tierlisteId);
            },
        });
    }

    private ladeTiereFuerImportFallback(tierlisteId: number): void {
        const params = new HttpParams().set('tierliste_id', tierlisteId.toString());
        this.api.getAll(this.apiBaseUrl + 'tierliste/read_tiere_fuer_import.php', params).subscribe({
            next: response => {
                this.selectedTiere = (response?.records || [])
                    .filter(t => Number(t?.source_gt_id) > 0)
                    .map(t => ({
                        gt_id: Number(t.source_gt_id),
                        tier_id: Number((t as any)?.tier_id) > 0 ? Number((t as any).tier_id) : undefined,
                        ohrmarke_notnull: t.ohrmarke_notnull || `Tier ${t.source_gt_id}`,
                        gewichtCtrl: new FormControl(),
                        currentGsId: undefined,
                    }));
            },
            error: err => {
                this.selectedTiere = [];
                this.messageService.errorMessage('Tiere fuer den Import konnten nicht geladen werden!', err);
            },
        });
    }

    private buildMostActualAbgangsgewichteRecords(): {
        gt_id: number;
        most_actual_abgangsgewicht: number;
        current_gs_id?: number;
    }[] {
        if (!this.gewichtModusCtrl.value || this.gewichtModusCtrl.value === 'total') {
            return [];
        }
        const allGewicht = this.toValidGewicht(this.gewichtAlleSelektiertCtrl.value);
        return this.selectedTiere
            .map(tier => {
                const ctrlGewicht = this.toValidGewicht(tier.gewichtCtrl.value);
                const gewicht =
                    this.gewichtModusCtrl.value === 'all' && allGewicht !== null ? allGewicht : ctrlGewicht;
                if (gewicht === null) {
                    return null;
                }
                return {
                    gt_id: Number(tier.gt_id),
                    most_actual_abgangsgewicht: gewicht,
                    current_gs_id: Number(tier?.currentGsId) > 0 ? Number(tier.currentGsId) : undefined,
                };
            })
            .filter(r => !!r && r.gt_id > 0);
    }

    private buildEinstallgewichteRecords(): { source_gt_id: number; source_tier_id?: number; einstallgewicht: number }[] {
        if (!this.gewichtModusCtrl.value) {
            return [];
        }
        if (this.gewichtModusCtrl.value === 'total') {
            const totalGewicht = this.toValidGewicht(this.gewichtGesamtCtrl.value);
            if (totalGewicht === null || !this.selectedTiere?.length) {
                return [];
            }
            const gewichtProTier = Math.round((totalGewicht / this.selectedTiere.length) * 100) / 100;
            if (gewichtProTier <= 0) {
                return [];
            }
            return this.selectedTiere
                .map(tier => ({
                    source_gt_id: Number(tier.gt_id),
                    source_tier_id: Number(tier?.tier_id) > 0 ? Number(tier.tier_id) : undefined,
                    einstallgewicht: gewichtProTier,
                }))
                .filter(r => r.source_gt_id > 0);
        }
        const allGewicht = this.toValidGewicht(this.gewichtAlleSelektiertCtrl.value);
        return this.selectedTiere
            .map(tier => {
                const ctrlGewicht = this.toValidGewicht(tier.gewichtCtrl.value);
                const gewicht =
                    this.gewichtModusCtrl.value === 'all' && allGewicht !== null ? allGewicht : ctrlGewicht;
                if (gewicht === null) {
                    return null;
                }
                return {
                    source_gt_id: Number(tier.gt_id),
                    source_tier_id: Number(tier?.tier_id) > 0 ? Number(tier.tier_id) : undefined,
                    einstallgewicht: gewicht,
                };
            })
            .filter(r => !!r && r.source_gt_id > 0);
    }

    private toValidGewicht(value: any): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const parsed = Number(String(value).replace(',', '.'));
        if (isNaN(parsed) || parsed <= 0) {
            return null;
        }
        return Math.round(parsed * 100) / 100;
    }
}
